import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { setLoading, setNotification } from 'state/app.state';

import callApiService from 'services/api/call.api.service';
import { ThunkAPI, CallTypes, AppCopyText, LeadTag, SystemErrors } from 'typings';
import { apiFetchLead } from 'state/lead.state';
import { apiErrorNotification } from 'utils/functions';

interface CallState {
  callType: CallTypes | null;
  incomingCall: boolean;
  liveTransferSuccess: boolean;
}

interface CallCenterFlowPayload {
  leadId: string;
}

/**
 * Asynchronous Actions (thunks)
 */
export const apiAcceptInboundCall = createAsyncThunk(
  'call/apiAcceptInboundCall',
  async (_, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;

      await callApiService.answerInboundCall(leadId);
    } catch (error: any) {
      const errorMessage =
        error.error?.statusCode === 410 ? AppCopyText.INBOUND_CALL_GONE : SystemErrors.CORE_API;

      thunkApi.dispatch(
        setNotification({
          text: errorMessage,
          type: 'error',
        })
      );

      return Promise.reject(errorMessage);
    }
  }
);

export const apiAttemptLiveTransfer = createAsyncThunk(
  'call/apiAttemptLiveTransfer',
  async (_, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;

      await callApiService.initiateLiveTransfer(leadId);
    } catch (error: any) {
      const statusCode = error.error?.statusCode;
      let errorMessage = '';

      switch (statusCode) {
        case 409:
          errorMessage = AppCopyText.LIVE_TRANSFER_UNSUCCESSFUL;
          break;
        case 410:
        case 422:
          errorMessage = error.error?.data?.message;
          break;
        default:
          errorMessage = SystemErrors.CORE_API;
      }

      thunkApi.dispatch(
        setNotification({
          text: errorMessage,
          type: 'error',
        })
      );
    }
  }
);

export const apiCancelLiveTransfer = createAsyncThunk(
  'call/apiCancelLiveTransfer',
  async (_, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;

      await callApiService.cancelLiveTransfer(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiMergeLiveTransferCall = createAsyncThunk(
  'call/apiMergeLiveTransferCall',
  async (_, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;

      await callApiService.mergeCall(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiInitiateCallCenterFlow = createAsyncThunk(
  'call/apiInitiateCallCenterFlow',
  async (payload: CallCenterFlowPayload, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      await callApiService.initiateCallCenterFlow(payload.leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

/* Call State Slice */
const callInitialState: CallState = {
  callType: null,
  incomingCall: false,
  liveTransferSuccess: false,
};

export const callSlice = createSlice({
  name: 'call',
  initialState: callInitialState,
  reducers: {
    setCallType: (state, action: PayloadAction<CallTypes | null>) => {
      state.callType = action.payload;
    },
    setIncomingCall: (state, action: PayloadAction<boolean>) => {
      state.incomingCall = action.payload;
    },
    setEndCall: (state) => {
      state.callType = null;
      state.incomingCall = false;
      state.liveTransferSuccess = false;
    },
    setLiveTransferSuccess: (state, action: PayloadAction<boolean>) => {
      state.liveTransferSuccess = action.payload;
    },
  },
  extraReducers: {
    [apiAcceptInboundCall.rejected.toString()]: (state, action) => {
      state.incomingCall = false;
    },
    [apiAttemptLiveTransfer.rejected.toString()]: (state) => {
      state.liveTransferSuccess = false;
    },
    [apiCancelLiveTransfer.fulfilled.toString()]: (state) => {
      state.liveTransferSuccess = false;
    },
    [apiMergeLiveTransferCall.fulfilled.toString()]: (state) => {
      state.liveTransferSuccess = false;
    },
    [apiFetchLead.fulfilled.toString()]: (state, action) => {
      const lead = action.payload;
      state.incomingCall = lead?.tag === LeadTag.INBOUND_CALL;
    },
  },
});

export const { setCallType, setIncomingCall, setEndCall, setLiveTransferSuccess } =
  callSlice.actions;

export default callSlice.reducer;
